import { RefObject, useRef } from 'react';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { ChatMessage, ChatMessageAttachment, StreamingActivity } from '../../interfaces/ChatInterface';
import { MetricOption } from '../../interfaces/MetricsInterface';
import { copyPlainText, copyRichText } from '../../utils/clipboard';
import { getChatSummaryUrl } from '../../services/chatService';
import useErrorModal from '../hooks/useErrorModal';
import { formatFileSize } from './chatUploads';
import { stripMetricTags } from './metricTags';
import ChatCopyButton from './ChatCopyButton';
import ChatMarkdown from './ChatMarkdown';
import ChatFileChip from './ChatFileChip';
import CheckmarkIcon from '../assets/green-checkmark.svg';
import FileIcon from '../assets/file-icon.svg';

interface MetricReferenceProps {
  metricOptions?: MetricOption[];
  onMetricSelect?: (metricValue: string) => void;
}

interface ChatMessageListProps extends MetricReferenceProps {
  messages: ChatMessage[];
  isPending: boolean;
  activity: StreamingActivity;
  isSummarizing?: boolean;
  eoaId: string;
  accessToken: string;
  scrollRef: RefObject<HTMLDivElement>;
}

const copyableHtml = (element: HTMLElement): string => {
  const clone = element.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('[data-chat-copy-exclude]').forEach((node) => node.remove());
  return clone.innerHTML;
};

const ThinkingIndicator = ({ label }: { label: string }) => (
  <div className="chat-message chat-message-assistant">
    <div className="chat-thinking">
      <span className="chat-thinking-dots">
        <span className="chat-thinking-dot" />
        <span className="chat-thinking-dot" />
        <span className="chat-thinking-dot" />
      </span>
      <span className="chat-thinking-text">{label}</span>
    </div>
  </div>
);

const AssistantMessage = ({ content, metricOptions, onMetricSelect }: { content: string } & MetricReferenceProps) => {
  const bodyRef = useRef<HTMLDivElement>(null);

  // The rendered DOM is copied as text/html so tables, lists and resolved chart images paste
  // into Google Docs / Word as real formatting; the raw markdown is the plain-text fallback.
  const handleCopy = async () => {
    if (!bodyRef.current) return;
    await copyRichText(copyableHtml(bodyRef.current), stripMetricTags(content));
  };

  return (
    <div className="chat-message chat-message-assistant">
      <div className="chat-message-bubble chat-message-bubble-assistant">
        <div ref={bodyRef}>
          <ChatMarkdown text={content} metricOptions={metricOptions} onMetricSelect={onMetricSelect} />
        </div>
        <ChatCopyButton onCopy={handleCopy} label="Copy" className="chat-copy-answer" />
      </div>
    </div>
  );
};

const SummaryMessage = ({
  message,
  eoaId,
  accessToken,
}: {
  message: ChatMessage;
  eoaId: string;
  accessToken: string;
}) => {
  const { handleOpen: handleOpenErrorModal } = useErrorModal();
  const attachment = message.attachments?.[0];

  const openSummary = async (disposition: 'inline' | 'attachment') => {
    if (!attachment) return;
    // Open the tab synchronously inside the click so pop-up blockers don't suppress it (a
    // window.open after the awaited fetch loses the user-activation and gets blocked). The
    // presigned URL is fetched next and the already-open tab is pointed at it.
    const opened = window.open('', '_blank');
    if (opened) opened.opener = null;
    try {
      const url = await getChatSummaryUrl(eoaId, attachment.id, disposition, accessToken);
      if (opened) {
        opened.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      opened?.close();
      handleOpenErrorModal('Failed to open the summary', error as Error);
    }
  };

  return (
    <div className="chat-message chat-message-assistant">
      <div className="chat-summary">
        <div className="chat-summary-heading">
          <img src={CheckmarkIcon} alt="" aria-hidden="true" className="chat-summary-check" />
          <span>Summary created</span>
        </div>
        <p className="chat-summary-intro">{message.content}</p>
        {attachment && (
          <>
            <ChatFileChip
              className="chat-summary-file"
              icon={<img src={FileIcon} alt="" aria-hidden="true" className="chat-file-chip-icon" />}
              name={attachment.original_file_name}
              secondary={formatFileSize(attachment.size_bytes)}
            />
            <div className="chat-summary-actions">
              <button type="button" className="chat-summary-action" onClick={() => void openSummary('inline')}>
                <OpenInNewIcon fontSize="small" />
                View
              </button>
              <button type="button" className="chat-summary-action" onClick={() => void openSummary('attachment')}>
                <FileDownloadOutlinedIcon fontSize="small" />
                Download
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const UserMessage = ({ content, attachments }: { content: string; attachments?: ChatMessageAttachment[] }) => (
  <div className="chat-message chat-message-user">
    <div className="chat-message-user-wrapper">
      {attachments && attachments.length > 0 && (
        <ul className="chat-message-attachments">
          {attachments.map((attachment) => (
            <li key={attachment.id} className="chat-message-attachment">
              <ChatFileChip
                icon={<InsertDriveFileOutlinedIcon className="chat-message-attachment-icon" />}
                name={attachment.original_file_name}
                secondary={formatFileSize(attachment.size_bytes)}
              />
            </li>
          ))}
        </ul>
      )}
      <div className="chat-message-bubble chat-message-bubble-user">{content}</div>
      <ChatCopyButton onCopy={() => copyPlainText(content)} label="Copy" className="chat-copy-question" />
    </div>
  </div>
);

const ChatMessageList = ({
  messages,
  isPending,
  activity,
  isSummarizing,
  eoaId,
  accessToken,
  scrollRef,
  metricOptions,
  onMetricSelect,
}: ChatMessageListProps) => {
  return (
    <div className="chat-message-list">
      <div className="chat-message-list-inner">
        {messages.map((message, index) => {
          if (message.kind === 'summary') {
            return <SummaryMessage key={index} message={message} eoaId={eoaId} accessToken={accessToken} />;
          }
          return message.role === 'user' ? (
            <UserMessage key={index} content={message.content} attachments={message.attachments} />
          ) : (
            <AssistantMessage
              key={index}
              content={message.content}
              metricOptions={metricOptions}
              onMetricSelect={onMetricSelect}
            />
          );
        })}

        {isPending && (activity.kind === 'idle' || activity.kind === 'tool') && (
          <ThinkingIndicator label={activity.kind === 'tool' ? activity.label : 'Thinking…'} />
        )}

        {(activity.kind === 'thinking' || activity.kind === 'answer') && (
          <div className="chat-message chat-message-assistant">
            <div className="chat-message-bubble chat-message-bubble-assistant">
              <ChatMarkdown text={activity.text} />
            </div>
          </div>
        )}

        {isSummarizing && <ThinkingIndicator label="Generating summary…" />}
        <div ref={scrollRef} className="chat-message-list-anchor" />
      </div>
    </div>
  );
};

export default ChatMessageList;
